import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../utils/analytics';
import { secondsUntil, TIME_WARNING_SECONDS } from '../utils/examTimer';
import { clampIndex, clearExamInProgress, countSavedAnswers, ExamInProgress, isExamTimeUp, mergeSavedAnswers, readExamInProgress, writeExamInProgress } from '../utils/examProgress';
import ExpiredExamChoice from '../components/ExpiredExamChoice';
import {
    ChevronLeft,
    ChevronRight,
    Flag,
    SkipForward,
    RotateCcw,
    Clock,
    List,
    AlertTriangle,
    Sparkles
} from 'lucide-react';
import {
    CertExamDefinition,
    CertQuestion,
    CertQuestionType,
    CertQuestionAnswer,
    CertAnswer
} from '@unity-learning/shared';
import { useUser } from '../hooks/useUser';
import { getCertificationExam, getCertificationExams, isApiError, submitCertificationExam } from '../services/api';
import {
    QuestionMatrix,
    QuestionMCQSingle,
    QuestionMCQMulti,
    QuestionMatchingDrag,
    QuestionOrdering,
    QuestionCodeDropdown,
    QuestionCodeInput,
    QuestionCodeConstruction,
    QuestionBoolean,
    QuestionPrompt
} from '../components/questions';
import { isAnswerComplete } from '../utils/examAnswerComplete';

// Timer hook : le temps restant vient d'une échéance fixée au démarrage (voir utils/examTimer).
function useCountdown(initialMinutes: number, onExpire: () => void) {
    const totalSeconds = initialMinutes * 60;
    const [deadline, setDeadline] = useState<number | null>(null);
    const [isRunning, setIsRunning] = useState(false);
    const [now, setNow] = useState(() => Date.now());
    const expiredRef = useRef(false);

    // Toujours la dernière version du rappel : l'envoi automatique doit voir l'examen et les réponses actuels.
    const onExpireRef = useRef(onExpire);
    onExpireRef.current = onExpire;

    useEffect(() => {
        if (!isRunning || deadline === null) return;

        const tick = () => {
            const t = Date.now();
            setNow(t);
            if (!expiredRef.current && secondsUntil(deadline, t) === 0) {
                expiredRef.current = true;
                onExpireRef.current();
            }
        };

        tick();
        const interval = setInterval(tick, 500);
        // Un onglet caché espace ses ticks : on recalcule dès que l'étudiant revient.
        document.addEventListener('visibilitychange', tick);
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', tick);
        };
    }, [isRunning, deadline]);

    // La pause (pendant l'envoi) arrête l'affichage, pas le temps réel : l'échéance ne bouge pas.
    // Une échéance explicite reprend un examen interrompu (F5, onglet fermé) sans rendre de temps.
    const start = (at?: number) => {
        setDeadline(d => d ?? at ?? Date.now() + totalSeconds * 1000);
        setIsRunning(true);
    };
    const pause = () => setIsRunning(false);

    const secondsRemaining = deadline === null ? totalSeconds : secondsUntil(deadline, now);
    const minutes = Math.floor(secondsRemaining / 60);
    const seconds = secondsRemaining % 60;
    const formatted = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

    return { secondsRemaining, minutes, seconds, formatted, start, pause, isRunning, deadline };
}

export const CertificationPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useUser();

    // Exam selection state
    interface AvailableExam {
        id: string;
        title: string;
        questionCount: number;
        durationMinutes: number;
        passingScorePercent: number;
        /** false quand le serveur a MOCK_EXAM_ENABLED=false : carte grisée, chargement refusé (403). */
        enabled?: boolean;
    }
    const [availableExams, setAvailableExams] = useState<AvailableExam[]>([]);
    const [selectedExamId, setSelectedExamId] = useState<string | null>(null);

    // Exam state
    const [exam, setExam] = useState<CertExamDefinition | null>(null);
    const [questions, setQuestions] = useState<CertQuestion[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [answers, setAnswers] = useState<Record<string, CertQuestionAnswer>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [examStarted, setExamStarted] = useState(false);
    const [startedAt, setStartedAt] = useState<string | null>(null);
    const [showSummary, setShowSummary] = useState(false);
    const [showConfirmFinish, setShowConfirmFinish] = useState(false);
    const [showTimeWarning, setShowTimeWarning] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Examen interrompu à reprendre (lu dans le navigateur) ; consommé une fois l'examen rechargé.
    const resumeRef = useRef<ExamInProgress | null>(null);
    const [resumed, setResumed] = useState(false);
    // Examen interrompu dont le temps est écoulé : l'élève choisit de le rendre ou de l'abandonner.
    const [expiredCopy, setExpiredCopy] = useState<ExamInProgress | null>(null);

    // Timer
    const timer = useCountdown(exam?.duration_minutes || 50, () => handleSubmitExam('timeout'));

    // Alerte des 5 minutes, une seule fois : un tick peut sauter la seconde 05:00 exacte.
    const timeWarningShownRef = useRef(false);
    useEffect(() => {
        if (examStarted && !timeWarningShownRef.current
            && timer.secondsRemaining > 0 && timer.secondsRemaining <= TIME_WARNING_SECONDS) {
            timeWarningShownRef.current = true;
            setShowTimeWarning(true);
        }
    }, [timer.secondsRemaining, examStarted]);

    // Fetch available exams list
    useEffect(() => {
        const fetchAvailableExams = async () => {
            try {
                setAvailableExams(await getCertificationExams<AvailableExam>());
            } catch (err) {
                console.error('Failed to fetch available exams:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAvailableExams();
    }, []);

    // Un examen commencé puis interrompu (F5, onglet fermé) reprend tout seul, avec son échéance.
    // Si l'échéance est passée, la reprise l'enverrait aussitôt : l'élève choisit d'abord.
    useEffect(() => {
        if (!user || availableExams.length === 0 || examStarted || selectedExamId || expiredCopy) return;
        const saved = readExamInProgress(user.id);
        if (!saved) return;
        const stillOffered = availableExams.some(e => e.id === saved.examId && e.enabled !== false);
        if (!stillOffered) return;
        if (isExamTimeUp(saved, Date.now())) {
            setExpiredCopy(saved);
            return;
        }
        resumeRef.current = saved;
        setSelectedExamId(saved.examId);
    }, [user, availableExams, examStarted, selectedExamId, expiredCopy]);

    // « Reprendre » : la copie est rechargée ; son échéance étant passée, elle part aussitôt (envoi « timeout »).
    const handleResumeExpired = () => {
        if (!expiredCopy) return;
        trackEvent('certification_expired_resumed');
        resumeRef.current = expiredCopy;
        setExpiredCopy(null);
        setSelectedExamId(expiredCopy.examId);
    };

    // « Abandonner » : la copie est effacée du navigateur ; le serveur n'en a jamais rien su.
    const handleAbandonExpired = () => {
        if (!user || !expiredCopy) return;
        trackEvent('certification_abandoned');
        clearExamInProgress(user.id);
        setExpiredCopy(null);
    };

    // Fetch selected exam data
    useEffect(() => {
        if (!selectedExamId) return;

        const fetchExam = async () => {
            setIsLoading(true);
            try {
                const examData = await getCertificationExam(selectedExamId);
                if (examData) {
                    setExam(examData);
                    setQuestions(examData.questions);

                    // Initialize answers
                    const initialAnswers: Record<string, CertQuestionAnswer> = {};
                    examData.questions.forEach((q: CertQuestion) => {
                        initialAnswers[q.id] = {
                            questionId: q.id,
                            response: getDefaultAnswer(q.type),
                            isFlagged: false,
                            isAnswered: false
                        };
                    });
                    const saved = resumeRef.current;
                    if (saved && saved.examId === examData.exam_id) {
                        resumeRef.current = null;
                        setAnswers(mergeSavedAnswers(initialAnswers, saved.answers));
                        setCurrentIndex(clampIndex(saved.currentIndex, examData.questions.length));
                        setStartedAt(saved.startedAt);
                        setExamStarted(true);
                        setResumed(true);
                        timer.start(saved.deadline);
                        trackEvent('certification_resumed');
                    } else {
                        setAnswers(initialAnswers);
                    }
                } else {
                    setError('Impossible de charger l\'examen');
                }
            } catch (err) {
                setError(isApiError(err) && !err.isNetworkError
                    ? err.message || 'Impossible de charger l\'examen'
                    : 'Erreur réseau lors du chargement de l\'examen');
            } finally {
                setIsLoading(false);
            }
        };

        fetchExam();
    }, [selectedExamId]);

    // Get default answer based on question type
    const getDefaultAnswer = (type: CertQuestionType): CertAnswer | null => {
        switch (type) {
            case CertQuestionType.TYPE_MCQ_SINGLE:
                return '';
            case CertQuestionType.TYPE_MCQ_MULTI:
            case CertQuestionType.TYPE_ORDERING:
                return [];
            case CertQuestionType.TYPE_MATRIX:
            case CertQuestionType.TYPE_MATCHING_DRAG:
            case CertQuestionType.TYPE_CODE_DROPDOWN:
            case CertQuestionType.TYPE_CODE_INPUT:
            case CertQuestionType.TYPE_CODE_CONSTRUCTION:
                return {};
            case CertQuestionType.TYPE_BOOLEAN:
                return null; // vrai ou faux une fois répondu
            default:
                return '';
        }
    };

    // Chaque réponse et chaque changement de question sont conservés dans le navigateur.
    useEffect(() => {
        if (!user || !exam || !examStarted || !startedAt || timer.deadline === null || isSubmitting) return;
        writeExamInProgress(user.id, { examId: exam.exam_id, startedAt, deadline: timer.deadline, answers, currentIndex });
    }, [user, exam, examStarted, startedAt, timer.deadline, answers, currentIndex, isSubmitting]);

    // Start exam
    const handleStartExam = () => {
        trackEvent('certification_started');
        setExamStarted(true);
        setStartedAt(new Date().toISOString());
        timer.start();
    };

    // Handle answer change
    const handleAnswerChange = (questionId: string, response: CertAnswer) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: {
                ...prev[questionId],
                response,
                isAnswered: isAnswerComplete(questions.find(q => q.id === questionId) ?? { type: '', content: {} }, response)
            }
        }));
    };

    // Toggle flag
    const handleToggleFlag = () => {
        const currentQ = questions[currentIndex];
        setAnswers(prev => ({
            ...prev,
            [currentQ.id]: {
                ...prev[currentQ.id],
                isFlagged: !prev[currentQ.id].isFlagged
            }
        }));
    };

    // Reset current question
    const handleResetQuestion = () => {
        const currentQ = questions[currentIndex];
        setAnswers(prev => ({
            ...prev,
            [currentQ.id]: {
                ...prev[currentQ.id],
                response: getDefaultAnswer(currentQ.type),
                isAnswered: false
            }
        }));
    };

    // Navigation
    const goToQuestion = (index: number) => {
        if (index >= 0 && index < questions.length) {
            setCurrentIndex(index);
            setShowSummary(false);
        }
    };

    // Submit exam
    const handleSubmitExam = async (submissionType: 'manual' | 'timeout' = 'manual') => {
        if (!exam || isSubmitting) return;
        setIsSubmitting(true);
        timer.pause();

        try {
            const result = await submitCertificationExam({
                examId: exam.exam_id,
                answers,
                startedAt,
                submissionType
            });
            if (user) clearExamInProgress(user.id);
            // L'id dans l'URL : un F5 sur la page de résultats recharge la copie au lieu de la perdre.
            navigate(`/certification/results/${result.attemptId}`, {
                state: {
                    result,
                    exam
                }
            });
        } catch (err) {
            setError(isApiError(err) && !err.isNetworkError
                ? err.message || 'Impossible d\'envoyer l\'examen'
                : 'Erreur réseau lors de l\'envoi de l\'examen');
            timer.start();
        } finally {
            setIsSubmitting(false);
        }
    };

    // Render question component based on type
    const renderQuestion = () => {
        const question = questions[currentIndex];
        if (!question) return null;

        const answer = answers[question.id];

        const commonProps = {
            disabled: false,
            onChange: (value: CertAnswer) => handleAnswerChange(question.id, value)
        };

        switch (question.type) {
            case CertQuestionType.TYPE_MATRIX:
                return (
                    <QuestionMatrix
                        question={question}
                        value={(answer?.response as Record<string, number>) || {}}
                        {...commonProps}
                    />
                );

            case CertQuestionType.TYPE_MCQ_SINGLE:
                return (
                    <QuestionMCQSingle
                        question={question}
                        value={(answer?.response as string) || null}
                        {...commonProps}
                    />
                );

            case CertQuestionType.TYPE_MATCHING_DRAG:
                return (
                    <QuestionMatchingDrag
                        question={question}
                        value={(answer?.response as Record<string, string>) || {}}
                        {...commonProps}
                    />
                );

            case CertQuestionType.TYPE_ORDERING:
                return (
                    <QuestionOrdering
                        question={question}
                        value={(answer?.response as string[]) || []}
                        {...commonProps}
                    />
                );

            case CertQuestionType.TYPE_CODE_DROPDOWN:
                return (
                    <QuestionCodeDropdown
                        question={question}
                        value={(answer?.response as Record<string, string>) || {}}
                        {...commonProps}
                    />
                );

            case CertQuestionType.TYPE_CODE_INPUT:
                return (
                    <QuestionCodeInput
                        question={question}
                        value={(answer?.response as Record<string, string>) || {}}
                        {...commonProps}
                    />
                );

            case CertQuestionType.TYPE_CODE_CONSTRUCTION:
                return (
                    <QuestionCodeConstruction
                        question={question}
                        value={(answer?.response as Record<string, string>) || {}}
                        {...commonProps}
                    />
                );

            case CertQuestionType.TYPE_BOOLEAN:
                return (
                    <QuestionBoolean
                        question={question}
                        value={answer?.response as unknown as boolean | null}
                        {...commonProps}
                    />
                );

            case CertQuestionType.TYPE_MCQ_MULTI:
                return (
                    <QuestionMCQMulti
                        question={question}
                        value={(answer?.response as string[]) || []}
                        {...commonProps}
                    />
                );

            default:
                return (
                    <div className="text-red-500">
                        Type de question non pris en charge : {question.type}
                    </div>
                );
        }
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-lg text-gray-600 dark:text-gray-400">
                    Chargement de l'examen…
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="text-red-500 mb-4">{error}</div>
                    <button
                        onClick={() => navigate('/')}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                    >
                        Retour à l'accueil
                    </button>
                </div>
            </div>
        );
    }

    // Pre-exam start screen
    if (!examStarted) {
        // If no exam selected, show exam selection
        if (!selectedExamId || !exam) {
            return (
                <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4">
                    <div className="max-w-4xl mx-auto">
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 text-center">
                            Entraînement à la certification
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 text-center mb-8">
                            Choisissez un examen pour commencer votre préparation à la certification
                        </p>

                        {expiredCopy ? (
                            <ExpiredExamChoice
                                examTitle={availableExams.find(e => e.id === expiredCopy.examId)?.title ?? 'Examen blanc'}
                                startedAt={expiredCopy.startedAt}
                                answeredCount={countSavedAnswers(expiredCopy)}
                                questionCount={availableExams.find(e => e.id === expiredCopy.examId)?.questionCount ?? Object.keys(expiredCopy.answers).length}
                                onResume={handleResumeExpired}
                                onAbandon={handleAbandonExpired}
                            />
                        ) : (
                        /* Exam Selection Grid */
                        <div className="grid gap-6 md:grid-cols-2">
                            {availableExams.filter(e => e.id !== 'debug_test').map((examOption) => {
                                const closed = examOption.enabled === false;
                                return (
                                <button
                                    key={examOption.id}
                                    onClick={closed ? undefined : () => setSelectedExamId(examOption.id)}
                                    disabled={closed}
                                    aria-disabled={closed}
                                    title={closed ? 'Examen blanc fermé pour le moment' : undefined}
                                    className={closed
                                        ? 'bg-white dark:bg-gray-800 rounded-xl shadow p-6 text-left border-2 border-dashed border-gray-300 dark:border-gray-600 opacity-60 cursor-not-allowed'
                                        : 'bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 text-left hover:shadow-xl hover:scale-[1.02] transition-all duration-200 border-2 border-transparent hover:border-blue-500'}
                                >
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                                        {examOption.title}
                                    </h3>
                                    {closed && (
                                        <div className="mb-3 px-2 py-1 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 text-xs font-medium rounded inline-block">
                                            🔒 Bientôt disponible — sera activé par votre formateur le jour de l'examen blanc
                                        </div>
                                    )}
                                    <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                                        <div className="flex items-center">
                                            <Clock className="w-4 h-4 mr-2" />
                                            <span>{examOption.durationMinutes} minutes</span>
                                        </div>
                                        <div className="flex items-center">
                                            <List className="w-4 h-4 mr-2" />
                                            <span>{examOption.questionCount} questions</span>
                                        </div>
                                        <div className="flex items-center">
                                            <Flag className="w-4 h-4 mr-2" />
                                            <span>Seuil de réussite : {examOption.passingScorePercent} %</span>
                                        </div>
                                    </div>
                                    {examOption.id === 'debug_test' && (
                                        <div className="mt-3 px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 text-xs rounded inline-block">
                                            🔧 Mode débogage
                                        </div>
                                    )}
                                </button>
                                );
                            })}

                            {/* AI Training Mode Option */}
                            <button
                                onClick={() => navigate('/ai-training')}
                                className="bg-gradient-to-br from-purple-600/10 to-blue-600/10 dark:from-purple-900/30 dark:to-blue-900/30 rounded-xl shadow-lg p-6 text-left hover:shadow-xl hover:scale-[1.02] transition-all duration-200 border-2 border-purple-300 dark:border-purple-700 hover:border-purple-500"
                            >
                                <div className="flex items-center gap-2 mb-3">
                                    <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                        Entraînement IA
                                    </h3>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                                    Des questions inédites générées par l'IA : chaque session crée de nouvelles questions, jamais vues auparavant.
                                </p>
                                <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                                    <div className="flex items-center">
                                        <Clock className="w-4 h-4 mr-2" />
                                        <span>50 minutes</span>
                                    </div>
                                    <div className="flex items-center">
                                        <List className="w-4 h-4 mr-2" />
                                        <span>40 questions générées par l'IA</span>
                                    </div>
                                </div>
                                <div className="mt-3 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs rounded inline-block">
                                    ✨ Propulsé par l'IA
                                </div>
                            </button>
                        </div>
                        )}
                    </div>
                </div>
            );
        }

        // Exam selected, show start screen
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="max-w-lg w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
                    <button
                        onClick={() => {
                            setSelectedExamId(null);
                            setExam(null);
                        }}
                        className="text-blue-600 dark:text-blue-400 text-sm mb-4 hover:underline flex items-center"
                    >
                        ← Retour au choix de l'examen
                    </button>

                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                        {exam?.title}
                    </h1>

                    <div className="space-y-4 mb-8">
                        <div className="flex items-center text-gray-600 dark:text-gray-400">
                            <Clock className="w-5 h-5 mr-3" />
                            <span>Durée : {exam?.duration_minutes} minutes</span>
                        </div>
                        <div className="flex items-center text-gray-600 dark:text-gray-400">
                            <List className="w-5 h-5 mr-3" />
                            <span>Questions : {questions.length}</span>
                        </div>
                        <div className="flex items-center text-gray-600 dark:text-gray-400">
                            <Flag className="w-5 h-5 mr-3" />
                            <span>Seuil de réussite : {exam?.passing_score_percent} %</span>
                        </div>
                    </div>

                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 mb-8">
                        <div className="flex items-start">
                            <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-2" />
                            <div className="text-sm text-yellow-800 dark:text-yellow-300">
                                Une fois l'examen commencé, le chronomètre ne peut pas être mis en pause. Assurez-vous d'avoir assez de temps pour le terminer.
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={handleStartExam}
                        className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Commencer l'examen
                    </button>
                </div>
            </div>
        );
    }

    const currentQuestion = questions[currentIndex];
    const currentAnswer = answers[currentQuestion?.id];
    const answeredCount = Object.values(answers).filter(a => a.isAnswered).length;
    const progressPercent = (answeredCount / questions.length) * 100;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="sticky top-0 z-40 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="w-full px-6 lg:px-12">
                    <div className="flex items-center justify-between h-16">
                        {/* Left: Navigation */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => goToQuestion(currentIndex - 1)}
                                disabled={currentIndex === 0}
                                title="Question précédente"
                                aria-label="Question précédente"
                                className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button
                                onClick={handleResetQuestion}
                                title="Réinitialiser la réponse"
                                aria-label="Réinitialiser la réponse"
                                className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                            >
                                <RotateCcw className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Center: Question Counter & Timer */}
                        <div className="flex items-center gap-6">
                            <div className="text-lg font-semibold text-gray-900 dark:text-white">
                                {currentIndex + 1}/{questions.length}
                            </div>

                            <div className={`
                flex items-center gap-2 px-4 py-2 rounded-full font-mono text-lg
                ${timer.minutes < 5
                                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                    : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                }
              `}>
                                <Clock className="w-5 h-5" />
                                {timer.formatted}
                            </div>
                        </div>

                        {/* Right: Actions */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleToggleFlag}
                                title="Marquer la question pour y revenir"
                                aria-label="Marquer la question pour y revenir"
                                className={`
                  px-3 py-1.5 text-sm rounded-lg transition-colors
                  ${currentAnswer?.isFlagged
                                        ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                    }
                `}
                            >
                                <Flag className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => goToQuestion(currentIndex + 1)}
                                disabled={currentIndex === questions.length - 1}
                                title="Passer la question"
                                aria-label="Passer la question"
                                className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                            >
                                <SkipForward className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => goToQuestion(currentIndex + 1)}
                                disabled={currentIndex === questions.length - 1}
                                title="Question suivante"
                                aria-label="Question suivante"
                                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setShowSummary(true)}
                                className="px-4 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                            >
                                Récapitulatif
                            </button>
                        </div>
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="h-1 bg-gray-200 dark:bg-gray-700">
                    <div
                        className="h-full bg-green-500 transition-all duration-300"
                        style={{ width: `${progressPercent}%` }}
                    />
                </div>
            </div>

            {/* Main Content - Full width with padding */}
            <div className="w-full px-6 lg:px-12 py-8">
                {showSummary ? (
                    // Summary View
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                            Récapitulatif de l'examen
                        </h2>

                        <div className="grid grid-cols-5 gap-2 mb-6">
                            {questions.map((q, idx) => {
                                const answer = answers[q.id];
                                return (
                                    <button
                                        key={q.id}
                                        onClick={() => goToQuestion(idx)}
                                        className={`
                      p-3 rounded-lg text-center font-medium
                      ${answer?.isAnswered
                                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                            }
                      ${answer?.isFlagged ? 'ring-2 ring-orange-500' : ''}
                      hover:opacity-80 transition-opacity
                    `}
                                    >
                                        {idx + 1}
                                        {answer?.isFlagged && <span className="ml-1">🚩</span>}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="flex justify-between items-center border-t border-gray-200 dark:border-gray-700 pt-4">
                            <div className="text-gray-600 dark:text-gray-400">
                                Répondues : {answeredCount}/{questions.length}
                            </div>
                            <button
                                onClick={() => setShowConfirmFinish(true)}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Terminer l'examen
                            </button>
                        </div>
                    </div>
                ) : (
                    // Question View
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                        {/* Question Prompt */}
                        <div className="mb-6">
                            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-2">
                                <span>Question {currentIndex + 1}</span>
                                {currentAnswer?.isFlagged && (
                                    <span className="text-orange-500">🚩 Marquée</span>
                                )}
                            </div>
                            {currentQuestion && (
                                <QuestionPrompt text={currentQuestion.prompt} className="text-lg" />
                            )}
                        </div>

                        {/* Question Component */}
                        <div className="mt-6">
                            {renderQuestion()}
                        </div>
                    </div>
                )}
            </div>

            {/* 5-minute Warning Modal */}
            {showTimeWarning && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md mx-4 animate-pulse">
                        <h3 className="text-xl font-bold text-yellow-600 dark:text-yellow-400 mb-2">
                            ⚠️ Plus que 5 minutes
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                            Il vous reste 5 minutes.
                        </p>
                        <button
                            onClick={() => setShowTimeWarning(false)}
                            className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            Continuer
                        </button>
                    </div>
                </div>
            )}

            {/* Confirm Finish Modal */}
            {showConfirmFinish && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md mx-4">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                            Terminer l'examen ?
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                            Cliquez sur « Terminer » pour clore l'examen et arrêter le chronomètre. Vous ne pourrez plus modifier vos réponses ensuite.
                        </p>
                        {answeredCount < questions.length && (
                            <p className="mb-4 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-sm font-medium">
                                ⚠️ {questions.length - answeredCount === 1
                                    ? 'Une question est encore sans réponse.'
                                    : `${questions.length - answeredCount} questions sont encore sans réponse.`}
                            </p>
                        )}
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowConfirmFinish(false)}
                                className="flex-1 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={() => handleSubmitExam('manual')}
                                disabled={isSubmitting}
                                className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            >
                                {isSubmitting ? 'Envoi…' : 'Terminer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CertificationPage;
